/**
 * RIM — IRA Recurring Contribution Flow (5-step wizard).
 *
 * Wave 5.3 IRA-FE-RECURRING — captures a standing instruction for periodic
 * IRA contributions and POSTs to /v3/ira/post-recurring-contribution. The
 * Function persists an IraRecurringSchedule row; the Wave 5.4 Timer-trigger
 * sweeps active schedules whose NextRunDate <= UTC today and fires the
 * actual jXRL contribution post.
 *
 * Layout mirrors `ContributionFlow.tsx` — same Stepper / WidgetCard shell,
 * same step pacing, same humanized affordances. NO codes, GUIDs, or Cart IDs
 * are surfaced: contribution kinds resolve via `IraReasonCode.DisplayName` /
 * the `KIND_OPTIONS` table, plan title resolves via `useIraDetail`, cadence
 * via `CADENCE_OPTIONS`.
 *
 * Steps:
 *   1. Tax year (current / prior)
 *   2. Contribution kind (11 admin-config'd kinds via IraContributionKind)
 *   3. Schedule — cadence + start/end dates + amount
 *   4. Funding source — ACH (routing+account) OR internal transfer
 *   5. Review (plain-English summary) → POST → confirmation
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, CardRadioGroup, KVPair, Pill, Stepper, WidgetCard } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type {
  IraContributionKind,
  IraRecurringCadence,
  IraRecurringFundingSource,
  IraRecurringContributionRequest,
  IraRecurringScheduleData,
  IraRecurringErrorCode,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

interface RecurringDraft {
  taxYear: number;
  kind: IraContributionKind;
  cadence: IraRecurringCadence;
  startDate: string;          // ISO yyyy-MM-dd
  endDate: string;            // '' when open-ended
  amount: string;             // string for input control
  fundingSource: IraRecurringFundingSource;
  achRouting: string;
  achAccount: string;
  internalAccountNumber: string;
  signatureMode: 'wet' | 'esign' | 'verbal';
}

const STEPS = [
  { label: 'Tax year' },
  { label: 'Contribution kind' },
  { label: 'Schedule' },
  { label: 'Funding source' },
  { label: 'Review & submit' },
];

const KIND_OPTIONS: Array<{
  value: IraContributionKind;
  primary: string;
  helper: string;
}> = [
  { value: 'TRADITIONAL_REGULAR',         primary: 'Traditional - regular',          helper: 'Annual Trad-IRA contribution within the regular / catch-up cap.' },
  { value: 'ROTH_REGULAR',                primary: 'Roth - regular',                 helper: 'Annual Roth contribution; subject to MAGI phaseout.' },
  { value: 'TRADITIONAL_TRANSFER_IN',     primary: 'Traditional - transfer in',      helper: 'Trustee-to-trustee transfer into a Trad-IRA.' },
  { value: 'ROTH_TRANSFER_IN',            primary: 'Roth - transfer in',             helper: 'Trustee-to-trustee transfer into a Roth IRA.' },
  { value: 'SEP_EMPLOYER',                primary: 'SEP - employer contribution',    helper: 'Employer-side SEP-IRA deposit; participant-age check skipped.' },
  { value: 'SIMPLE_EMPLOYEE',             primary: 'SIMPLE - employee deferral',     helper: 'Employee elective deferral into a SIMPLE IRA.' },
  { value: 'SIMPLE_EMPLOYER_MATCH',       primary: 'SIMPLE - employer match',        helper: 'Employer-side SIMPLE match.' },
  { value: 'SIMPLE_EMPLOYER_NONELECTIVE', primary: 'SIMPLE - employer non-elective', helper: 'Employer non-elective SIMPLE contribution.' },
];

const CADENCE_OPTIONS: Array<{
  value: IraRecurringCadence;
  primary: string;
  helper: string;
}> = [
  { value: 'weekly',      primary: 'Every week',                       helper: 'Same day-of-week, every 7 days.' },
  { value: 'biweekly',    primary: 'Every 2 weeks',                    helper: 'Useful for paycheck-aligned funding.' },
  { value: 'semimonthly', primary: 'Twice a month (1st & 15th)',       helper: 'Two posts per month on the 1st and the 15th.' },
  { value: 'monthly',     primary: 'Every month',                      helper: 'Same day-of-month, monthly.' },
  { value: 'quarterly',   primary: 'Every 3 months',                   helper: 'Same day-of-month, every quarter.' },
];

const FUNDING_OPTIONS: Array<{
  value: IraRecurringFundingSource;
  primary: string;
  helper: string;
}> = [
  { value: 'ach',               primary: 'ACH from external bank',  helper: 'Pulled from an outside checking/savings account using routing + account.' },
  { value: 'internal-transfer', primary: 'Transfer from PCB account', helper: 'Pulled from another PCB deposit account on the same customer.' },
];

const SIGNATURE_OPTIONS: Array<{ value: RecurringDraft['signatureMode']; label: string }> = [
  { value: 'wet',    label: 'Wet signature on file' },
  { value: 'esign',  label: 'Electronic signature' },
  { value: 'verbal', label: 'Verbal authorization (officer attests)' },
];

/** Cadence -> friendly weekday/cadence-suffix used in the plain-English review. */
function cadenceSummary(cadence: IraRecurringCadence, startISO: string): string {
  if (!startISO) return '';
  // Build a UTC date so timezone shifts don't move us off the chosen day.
  const d = new Date(startISO + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
  const dayOfMonth = d.getUTCDate();
  switch (cadence) {
    case 'weekly':       return `each ${weekday}`;
    case 'biweekly':     return `every other ${weekday}`;
    case 'semimonthly':  return 'on the 1st and 15th of each month';
    case 'monthly':      return `on the ${ordinal(dayOfMonth)} of each month`;
    case 'quarterly':    return `on the ${ordinal(dayOfMonth)} of every third month`;
  }
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

function formatDateLong(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function maskTail(s: string, keep = 4): string {
  if (!s) return '';
  if (s.length <= keep) return s;
  return `-${s.slice(-keep)}`;
}

function todayISO(): string {
  const t = new Date();
  const yyyy = t.getFullYear();
  const mm = String(t.getMonth() + 1).padStart(2, '0');
  const dd = String(t.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function RecurringContributionFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<RecurringDraft>({
    taxYear: currentYear,
    kind: 'TRADITIONAL_REGULAR',
    cadence: 'monthly',
    startDate: todayISO(),
    endDate: '',
    amount: '',
    fundingSource: 'internal-transfer',
    achRouting: '',
    achAccount: '',
    internalAccountNumber: '',
    signatureMode: 'wet',
  });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<IraRecurringErrorCode | null>(null);
  const [postedData, setPostedData] = useState<IraRecurringScheduleData | null>(null);

  const planTitle = useMemo<string>(() => {
    if (!ira) return planNumber ?? '';
    return ira.planDescription ?? ira.planCodeRaw ?? planNumber ?? '';
  }, [ira, planNumber]);

  const kindLabel = useMemo<string>(() => {
    return KIND_OPTIONS.find((k) => k.value === draft.kind)?.primary ?? draft.kind;
  }, [draft.kind]);

  const cadenceLabel = useMemo<string>(() => {
    return CADENCE_OPTIONS.find((c) => c.value === draft.cadence)?.primary ?? draft.cadence;
  }, [draft.cadence]);

  const fundingLabel = useMemo<string>(() => {
    return FUNDING_OPTIONS.find((f) => f.value === draft.fundingSource)?.primary ?? draft.fundingSource;
  }, [draft.fundingSource]);

  if (!planNumber) {
    return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before scheduling a recurring contribution." />;
  }
  if (loadingIra) {
    return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  }
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const amountNum = Number.parseFloat(draft.amount);
  const officerCode =
    user?.officerCode
    ?? (typeof user?.upn === 'string' ? user.upn : undefined)
    ?? '';

  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return Number.isInteger(draft.taxYear) && draft.taxYear >= 1900;
      case 1: return !!draft.kind;
      case 2: {
        if (!draft.cadence) return false;
        if (!draft.startDate) return false;
        if (!Number.isFinite(amountNum) || amountNum <= 0) return false;
        if (draft.endDate && draft.endDate < draft.startDate) return false;
        return true;
      }
      case 3: {
        if (draft.fundingSource === 'ach') {
          return draft.achRouting.trim().length > 0 && draft.achAccount.trim().length > 0;
        }
        return draft.internalAccountNumber.trim().length > 0;
      }
      case 4: return submitState === 'idle' || submitState === 'error';
      default: return false;
    }
  })();

  const submit = async (): Promise<void> => {
    if (!officerCode) {
      setSubmitError('Officer code missing from session — re-authenticate before scheduling.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }
    if (!ira.cifId) {
      setSubmitError('IRA owner CIF unavailable on this plan — cannot schedule.');
      setSubmitErrorCode('MISSING_CIF');
      setSubmitState('error');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    try {
      const body: IraRecurringContributionRequest = {
        cif: ira.cifId,
        planAccountNumber: ira.planNumber,
        taxYear: draft.taxYear,
        contributionType: draft.kind,
        recurrence: {
          cadence: draft.cadence,
          startDate: draft.startDate,
          ...(draft.endDate ? { endDate: draft.endDate } : {}),
          amount: amountNum,
          fundingSource: draft.fundingSource,
          ...(draft.fundingSource === 'ach'
            ? {
                achRouting: draft.achRouting.trim(),
                achAccount: draft.achAccount.trim(),
              }
            : {
                internalAccountNumber: draft.internalAccountNumber.trim(),
              }),
        },
        officerCode,
        signatureMode: draft.signatureMode,
      };

      const res = await client.retail.ira.postRecurringContribution(body);
      if (!res.success) {
        setSubmitError(res.error);
        setSubmitErrorCode(res.code);
        setSubmitState('error');
        return;
      }

      setPostedData(res.data);
      emit({
        eventType: 'ira-recurring-contribution-scheduled',
        eventCategory: 'cif-account',
        severity: 'info',
        context: `plan:${ira.planNumber}`,
        payload: {
          planNumber: ira.planNumber,
          planCode: ira.planCode,
          kind: draft.kind,
          cadence: draft.cadence,
          startDate: draft.startDate,
          endDate: draft.endDate || null,
          amount: amountNum,
          taxYear: draft.taxYear,
          fundingSource: draft.fundingSource,
          // Note: scheduleId is intentionally NOT included in the user-visible
          // event payload (Bill 2026-04-25 "no GUIDs surfaced"). It's persisted
          // in the schedule row and recoverable via the audit log.
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Recurring schedule create failed.');
      setSubmitErrorCode('POST_FAILED');
      setSubmitState('error');
    }
  };

  const renderErrorBanner = (): JSX.Element | null => {
    if (!submitError) return null;
    const friendly = ((): string => {
      switch (submitErrorCode) {
        case 'INVALID_CADENCE':         return 'Choose a valid cadence (weekly, biweekly, twice-monthly, monthly, or quarterly).';
        case 'INVALID_START_DATE':      return 'Start date is invalid — pick a calendar date.';
        case 'INVALID_END_DATE':        return 'End date must be on or after the start date.';
        case 'INVALID_AMOUNT':          return 'Amount must be a positive dollar value.';
        case 'INVALID_FUNDING_SOURCE':  return 'Pick a funding source — ACH or internal transfer.';
        case 'MISSING_ACH_DETAILS':     return 'ACH funding requires both routing and account numbers.';
        case 'MISSING_INTERNAL_ACCOUNT':return 'Internal-transfer funding requires the source PCB account number.';
        case 'INVALID_CONTRIBUTION_KIND':return 'Pick a contribution kind that this IRA plan supports.';
        case 'MISSING_OFFICER':         return submitError;
        case 'MISSING_CIF':             return submitError;
        default:                         return submitError;
      }
    })();
    return <div className="rim-rmd-banner is-warn" role="alert">{friendly}</div>;
  };

  const fundingDescription = ((): string => {
    if (draft.fundingSource === 'ach') {
      const tail = maskTail(draft.achAccount);
      return tail ? `ACH from external account ending in ${tail}` : 'ACH from an external bank account';
    }
    const tail = maskTail(draft.internalAccountNumber);
    return tail ? `your PCB account ending in ${tail}` : 'your PCB account';
  })();

  const summary = ((): string => {
    if (!draft.startDate || !Number.isFinite(amountNum) || amountNum <= 0) return '';
    const start = formatDateLong(draft.startDate);
    const end = draft.endDate ? `until ${formatDateLong(draft.endDate)}` : 'until further notice';
    const amt = `$${amountNum.toFixed(2)}`;
    const cad = cadenceSummary(draft.cadence, draft.startDate);
    return `Starting ${start}, ${amt} from ${fundingDescription} will be deposited to ${planTitle} ${cad} ${end}.`;
  })();

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>
          Recurring IRA contribution &middot; {planTitle} &middot; {ira.planNumber}
        </h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Tax year" stripe="compliance">
          <p className="rim-flow__helper">
            Pick the tax year each scheduled post will apply to. Open-ended schedules
            roll their tax year forward at the start of each new year automatically
            (Wave 5.4 sweeper handles year transitions).
          </p>
          <div className="rim-flow__year-row">
            {[currentYear, currentYear - 1].map((y) => (
              <Button
                key={y}
                variant={draft.taxYear === y ? 'primary' : 'ghost'}
                onClick={() => setDraft({ ...draft, taxYear: y })}
              >
                {y}
              </Button>
            ))}
          </div>
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Contribution kind" stripe="compliance">
          <CardRadioGroup<IraContributionKind>
            name="rim-recurring-kind"
            selected={draft.kind}
            onChange={(v) => setDraft({ ...draft, kind: v })}
            options={KIND_OPTIONS}
          />
          {ira.planCode !== 'SEP' && draft.kind === 'SEP_EMPLOYER' && (
            <div className="rim-rmd-banner is-warn" role="alert">
              This plan is not a SEP - SEP_EMPLOYER scheduled posts will be rejected by Core Director when the sweeper fires them.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Schedule" stripe="compliance">
          <p className="rim-flow__helper">
            Choose how often the contribution should post. The first run is the
            start date you pick; later runs follow the cadence automatically.
          </p>
          <CardRadioGroup<IraRecurringCadence>
            name="rim-recurring-cadence"
            selected={draft.cadence}
            onChange={(v) => setDraft({ ...draft, cadence: v })}
            options={CADENCE_OPTIONS}
          />
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">First run (start date)</span>
              <input
                type="date"
                className="rim-benef-field"
                value={draft.startDate}
                min={todayISO()}
                onChange={(e) => setDraft({ ...draft, startDate: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">End date (optional)</span>
              <input
                type="date"
                className="rim-benef-field"
                value={draft.endDate}
                min={draft.startDate || todayISO()}
                onChange={(e) => setDraft({ ...draft, endDate: e.currentTarget.value })}
              />
              <span className="rim-flow__hint">Leave blank for &quot;until further notice&quot;.</span>
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount per run (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Funding source" stripe="compliance">
          <p className="rim-flow__helper">
            Pull funds from an outside bank via ACH, or pull from another PCB
            deposit account belonging to the same customer.
          </p>
          <CardRadioGroup<IraRecurringFundingSource>
            name="rim-recurring-funding"
            selected={draft.fundingSource}
            onChange={(v) => setDraft({ ...draft, fundingSource: v })}
            options={FUNDING_OPTIONS}
          />
          {draft.fundingSource === 'ach' && (
            <div className="rim-flow__grid-2">
              <label className="rim-flow__field">
                <span className="rim-flow__field-label">External bank routing number</span>
                <input
                  type="text"
                  className="rim-benef-field"
                  value={draft.achRouting}
                  inputMode="numeric"
                  maxLength={9}
                  onChange={(e) => setDraft({ ...draft, achRouting: e.currentTarget.value })}
                />
              </label>
              <label className="rim-flow__field">
                <span className="rim-flow__field-label">External bank account number</span>
                <input
                  type="text"
                  className="rim-benef-field"
                  value={draft.achAccount}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, achAccount: e.currentTarget.value })}
                />
              </label>
            </div>
          )}
          {draft.fundingSource === 'internal-transfer' && (
            <div className="rim-flow__grid-2">
              <label className="rim-flow__field">
                <span className="rim-flow__field-label">Source PCB account number</span>
                <input
                  type="text"
                  className="rim-benef-field"
                  value={draft.internalAccountNumber}
                  inputMode="numeric"
                  placeholder="e.g. 1234567890"
                  onChange={(e) => setDraft({ ...draft, internalAccountNumber: e.currentTarget.value })}
                />
                <span className="rim-flow__hint">
                  PCB checking, savings, or money-market account on the same customer.
                  An admin-configured account picker will replace this field in a follow-on wave.
                </span>
              </label>
            </div>
          )}
          <label className="rim-flow__field">
            <span className="rim-flow__field-label">Authorization on file</span>
            <select
              className="rim-benef-field"
              value={draft.signatureMode}
              onChange={(e) => setDraft({ ...draft, signatureMode: e.currentTarget.value as RecurringDraft['signatureMode'] })}
              aria-label="Authorization signature"
            >
              {SIGNATURE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </WidgetCard>
      )}

      {step === 4 && (
        <WidgetCard title="Review &amp; submit" stripe="compliance">
          <div className="rim-rmd-banner" role="status" style={{ marginBottom: 'var(--sp-4)' }}>
            <strong>In plain English:</strong> {summary || 'Complete the prior steps to see a summary.'}
          </div>

          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${planTitle} - ${ira.planNumber}`} />
            <KVPair label="Owner CIF" value={ira.cifId ?? '—'} />
            <KVPair label="Tax year" value={draft.taxYear} />
            <KVPair label="Contribution kind" value={kindLabel} />
            <KVPair label="Cadence" value={cadenceLabel} />
            <KVPair label="First run" value={formatDateLong(draft.startDate)} />
            <KVPair label="Ends" value={draft.endDate ? formatDateLong(draft.endDate) : <Pill variant="info">until further notice</Pill>} />
            <KVPair label="Amount per run" value={Number.isFinite(amountNum) ? `$${amountNum.toFixed(2)}` : '—'} />
            <KVPair label="Funding" value={fundingLabel} />
            <KVPair
              label="From account"
              value={draft.fundingSource === 'ach'
                ? (draft.achAccount ? `external account ending ${maskTail(draft.achAccount)}` : '—')
                : (draft.internalAccountNumber ? `PCB account ending ${maskTail(draft.internalAccountNumber)}` : '—')}
            />
            <KVPair label="Authorization" value={SIGNATURE_OPTIONS.find((s) => s.value === draft.signatureMode)?.label ?? draft.signatureMode} />
          </div>

          {submitState === 'submitted' && postedData && (
            <div className="rim-rmd-banner" role="status" style={{ marginTop: 'var(--sp-4)' }}>
              Recurring contribution scheduled. The first deposit will run on{' '}
              <strong>{formatDateLong(postedData.nextRunDate.slice(0, 10))}</strong>.
              You can pause or cancel the schedule from the plan&apos;s recurring-contributions panel.
            </div>
          )}

          {renderErrorBanner()}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button variant="primary" disabled={submitState === 'submitting'} onClick={() => { void submit(); }}>
            {submitState === 'submitting' ? 'Scheduling…' : 'Schedule recurring contribution'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
